import { FieldConfig } from "@core/components/table/edit-fields";
import {
  DistinctFilterMetadata,
  ProductType,
  RankingMetric,
} from "creso-core/types";
import { format, parse } from "date-fns";
import { BarChartIcon, ShieldIcon, UsersIcon } from "lucide-react";
import { getProductLabel } from "creso-core/constants";
import { HistoricalNavChart } from "./components/funds-compare/historical-returns-chart";
import { FundInfo } from "./components/funds-compare/fund-info";
import { HistoricalReturns } from "./components/funds-compare/historical-returns";
import { FundScore } from "./components/funds-compare/fund-score";
import { AssetAllocation } from "./components/funds-compare/asset-allocation";
import { FundOverlap } from "./components/funds-compare/fund-overlap";
import { TopHoldings } from "./components/funds-compare/top-holdings";

const DEFAULT_RANGE_FORMATTER_IN = (val: string | number) =>
  typeof val === "string" ? parseFloat(parseFloat(val).toFixed(2)) : val;

const DEFAULT_RANGE_FORMATTER_OUT = (val: string | number) => val.toString();

interface FilterFieldConfig extends FieldConfig {
  /** Corresponds to the key that needs to be passed to metadata api to get the allowed values */
  fieldId?: string;
}

export interface MultiFilterConfig extends FilterFieldConfig {
  type: "MULTI";
  fieldId: string;
  /** Filter Key that needs to be passed to the listing api */
  filterKey: string;
  formatter?: (label: string) => string;
  order?: (
    values: DistinctFilterMetadata["values"]
  ) => DistinctFilterMetadata["values"];
}

interface RangeFilterConfig extends FilterFieldConfig {
  type: "RANGE";
  filterKey: { min: string; max: string };
  formatter: {
    in: (val: number | string) => number;
    out: (val: number | string) => string;
    unit?: string;
  };
  step?: number;
  min?: number;
  max?: number;
}

interface ToggleFilterConfig extends FilterFieldConfig {
  type: "TOGGLE";
  filterKey: string;
}

export type FilterConfig =
  | MultiFilterConfig
  | RangeFilterConfig
  | ToggleFilterConfig;

export const FILTER_CONFIG: FilterConfig[] = [
  {
    id: "provider_company_name",
    fieldId: "provider_company_name",
    filterKey: "provider_company_id_in",
    label: "AMC",
    group: "General",
    configurable: false,
    visible: true,
    type: "MULTI",
  },
  {
    id: "product_type",
    fieldId: "product_type",
    filterKey: "product_type",
    label: "Product",
    group: "General",
    configurable: false,
    visible: true,
    type: "MULTI",
    formatter: (label: string) => getProductLabel(label as ProductType),
    order: (val: DistinctFilterMetadata["values"]) => {
      const mf = val.find((v) => v.name === "MF");
      const gift = val.find((v) => v.name === "GIFT");
      const sif = val.find((v) => v.name === "SIF");
      return [mf, sif, gift] as DistinctFilterMetadata["values"];
    },
  },
  {
    id: "fund_level_category_name",
    fieldId: "fund_level_category_name",
    filterKey: "fund_level_category_code_in",
    label: "Category",
    group: "General",
    configurable: false,
    visible: true,
    type: "MULTI",
  },
  {
    id: "risk_level",
    fieldId: "risk_level",
    filterKey: "risk_level_in",
    label: "Risk",
    group: "General",
    configurable: true,
    visible: false,
    type: "MULTI",
  },
  {
    id: "is_index_fund",
    label: "Index funds",
    fieldId: "is_index_fund",
    filterKey: "is_index_fund",
    group: "General",
    configurable: true,
    visible: true,
    type: "TOGGLE",
  },
  {
    id: "inception_date",
    filterKey: { min: "inception_date_to", max: "inception_date_from" },
    formatter: {
      out: (val: number | string) => {
        const outVal = Number(val);
        const today = new Date();
        return format(
          today.setFullYear(today.getFullYear() - outVal),
          "yyyy-MM-dd"
        );
      },
      in: (val: string | number) => {
        if (typeof val === "number") return val;
        const date = parse(val, "yyyy-MM-dd", new Date());
        const today = new Date();
        return today.getFullYear() - date.getFullYear();
      },
      unit: "yr",
    },
    label: "Fund Age",
    group: "General",
    configurable: true,
    visible: false,
    type: "RANGE",
    min: 0,
    max: 40,
  },
  {
    id: "assets_under_management",
    label: "AUM",
    fieldId: "assets_under_management",
    filterKey: { min: "min_aum", max: "max_aum" },
    group: "General",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: (val: number | string) => Math.round(Number(val) / 1_00_00_000),
      out: (val: number | string) => `${Number(val) * 1_00_00_000}`,
      unit: "Cr",
    },
    step: 1,
  },
  {
    id: "performance__return_3_year",
    label: "3Y Trailing Return",
    fieldId: "return_3_year",
    filterKey: {
      min: "min_return_3_year",
      max: "max_return_3_year",
    },
    group: "Returns",
    configurable: true,
    visible: true,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__return_5_year",
    label: "5Y Trailing Return",
    fieldId: "return_5_year",
    filterKey: { min: "min_return_5_year", max: "max_return_5_year" },
    group: "Returns",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__return_10_year",
    label: "10Y Trailing Return",
    fieldId: "return_10_year",
    filterKey: { min: "min_return_10_year", max: "max_return_10_year" },
    group: "Returns",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__avg_rolling_return_3_year",
    label: "3Y Rolling Return",
    fieldId: "avg_rolling_return_3_year",
    filterKey: {
      min: "min_avg_rolling_return_3_year",
      max: "max_avg_rolling_return_3_year",
    },
    group: "Returns",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__avg_rolling_return_5_year",
    fieldId: "avg_rolling_return_5_year",
    filterKey: {
      min: "min_avg_rolling_return_5_year",
      max: "max_avg_rolling_return_5_year",
    },
    label: "5Y Rolling Return",
    group: "Returns",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__avg_rolling_return_10_year",
    fieldId: "avg_rolling_return_10_year",
    filterKey: {
      min: "min_avg_rolling_return_10_year",
      max: "max_avg_rolling_return_10_year",
    },
    label: "10Y Rolling Return",
    group: "Returns",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__sharpe_ratio_3_year",
    fieldId: "sharpe_ratio_3_year",
    filterKey: {
      min: "min_sharpe_ratio_3_year",
      max: "max_sharpe_ratio_3_year",
    },
    label: "Sharpe Ratio",
    group: "Performance",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
  {
    id: "performance__standard_deviation_3_year",
    fieldId: "standard_deviation_3_year",
    filterKey: {
      min: "min_standard_deviation_3_year",
      max: "max_standard_deviation_3_year",
    },
    label: "Standard Deviation",
    group: "Performance",
    configurable: true,
    visible: false,
    type: "RANGE",
    formatter: {
      in: DEFAULT_RANGE_FORMATTER_IN,
      out: DEFAULT_RANGE_FORMATTER_OUT,
      unit: "%",
    },
  },
];

export const FUND_SCORE_CONFIG = [
  {
    id: "management",
    title: "Management",
    icon: <UsersIcon className="size-4" />,
    metrics: ["manager_tenure", "ter"] as RankingMetric[],
    metricsKey: "top_fund_management" as const,
    scoreKey: "score_fund_management" as const,
  },
  {
    id: "performance",
    title: "Performance",
    icon: <BarChartIcon className="size-4" />,
    scoreKey: "score_performance" as const,
    metricsKey: "top_performance" as const,
    metrics: [
      "relative_return_3yr",
      "relative_return_5yr",
      "rolling_return_3yr",
      "rolling_return_5yr",
    ] as RankingMetric[],
  },
  {
    id: "risk-reward",
    title: "Risk Reward",
    scoreKey: "score_risk_reward" as const,
    icon: <ShieldIcon className="size-4" />,
    metricsKey: "top_risk_reward" as const,
    metrics: [
      "standard_deviation_3yr",
      "sharpe_ratio_3yr",
      "capture_ratio_downside_3yr",
      "capture_ratio_upside_3yr",
    ] as RankingMetric[],
  },
];

export const REPORT_SECTIONS = [
  {
    id: "returns-backtest",
    label: "Returns Backtest",
    component: HistoricalNavChart,
  },
  {
    id: "fund-info",
    label: "Fund Info",
    component: FundInfo,
  },
  {
    id: "historical-returns",
    label: "Historical Returns",
    component: HistoricalReturns,
  },
  {
    id: "fund-score",
    label: "Fund Score",
    component: FundScore,
  },
  {
    id: "asset-allocation",
    label: "Asset Allocation",
    component: AssetAllocation,
  },
  {
    id: "portfolio-overlap",
    label: "Portfolio Overlap",
    component: FundOverlap,
  },
  {
    id: "top-holdings",
    label: "Top 10 Holdings",
    component: TopHoldings,
  },
];
